<script lang="ts">
  import type { Snippet } from 'svelte';

  /**
   * The frame every admin page sits in: FULL WIDTH, one padding, no exceptions.
   *
   * WHAT IT FIXES. Twenty pages had each written their own container, and they had
   * drifted into EIGHT widths (`max-w-3xl`, `4xl`, `5xl`, `6xl`, `54.6rem`, `92rem`,
   * `100rem`, `120rem`) and five padding combinations (`px-4`/`px-5`/`px-6`,
   * `py-6`/`py-7`/`py-8`, some with `sm:` steps and some without). One page had no
   * container at all. The cost was visible on every navigation: the heading, the rule
   * under it and the page's left edge all landed somewhere different on the next page.
   *
   * WHY THERE IS NO WIDTH KNOB. There were two before this, and the reason there is
   * none now is worth keeping, because the idea is tempting and it fails twice.
   *
   * A capped, centred frame means the content's left edge is a function of the VIEWPORT
   * (`(100vw - cap) / 2`), not of the layout. Two pages with different caps therefore
   * start in different places, and the community strip in `AdminShell` — which sits
   * directly above this and reads as part of the same block — starts in a third. Every
   * navigation nudged the heading sideways.
   *
   * Making the strip follow the page's cap only moved the problem: now the SELECTOR
   * jumped between pages instead. Anything the shell and the page must agree on cannot
   * be a per-page choice. The agreement has to be a constant, and the only constant that
   * needs no negotiation is the edge itself.
   *
   * So: no `max-w`, no `mx-auto`. The content column is the window minus the rail, and
   * the left edge is `px-5` from it on EVERY page and in the strip above. Nothing to
   * publish, nothing to sync, nothing to jump.
   *
   * WHAT THIS COSTS. A form row or a paragraph is not more readable at 1800px, and
   * pages that hold those keep their own measure INSIDE their cards (`max-w-xl` on a
   * paragraph, a bounded input) — a left-aligned measure inside a full-width card, which
   * is not the same thing as a centred page and does not move when you navigate.
   *
   * A PLAIN BLOCK by default, and `fill` (a flex column) only where a page asked for it.
   * An earlier version made every page a flex column because two of them needed it, which
   * silently changed how the other seventeen lay their children out — flex items do not
   * collapse margins and do not size like blocks, and none of those pages had been
   * written against those rules. A frame should not change what it frames.
   *
   * `fill` is for the two pages whose panes fill the viewport (Conversations, the Inbox
   * explorer). Those give their pane row `min-h-0 flex-1` and get a full-height split
   * without measuring anything in JS.
   */
  let {
    children,
    fill = false,
    class: klass = '',
  }: {
    children: Snippet;
    /** Lay the frame out as a flex column so a child can claim the leftover height.
     *  Only the two pane pages want this. */
    fill?: boolean;
    /** Escape hatch for the pane pages, which add `overflow-hidden`. Never a width. */
    class?: string;
  } = $props();
</script>

<div class="w-full flex-1 px-5 py-8 sm:px-6 {fill ? 'flex flex-col' : ''} {klass}">
  {@render children()}
</div>
